/**
 * Why the encoder is holding back — straight from the browser, which knows.
 *
 * `qualityLimitationReason` is the single most actionable field on an outbound
 * video stat: the browser tells you whether it is limiting because of the
 * network ('bandwidth'), the machine ('cpu'), or not at all ('none'). It is
 * exactly the signal that would replace our core-counting guess at "weak
 * device", and until now it was named once, in a comment, and read nowhere.
 *
 * This module only measures. Nothing changes behaviour off the back of it yet,
 * deliberately: the threshold question — how much sustained 'cpu' limiting
 * should cause us to stop publishing three encodes — needs data from real calls
 * that we do not have. See VIDEO_TRANSMISSION_ANALYSIS.md §8.3. Measure first,
 * then decide, in that order.
 */

export type QualityLimitationReason = 'none' | 'cpu' | 'bandwidth' | 'other'

/** Ordered worst-first, so aggregating across simulcast rows is a min-index. */
export const REASONS: readonly QualityLimitationReason[] = ['cpu', 'bandwidth', 'other', 'none']

export type LimitationDurations = Record<Exclude<QualityLimitationReason, 'none'>, number>

export const ZERO_DURATIONS: LimitationDurations = { cpu: 0, bandwidth: 0, other: 0 }

/** Anything the browser reports that we do not recognise is 'other', not a crash. */
export function normalizeReason(raw: unknown): QualityLimitationReason {
  return raw === 'cpu' || raw === 'bandwidth' || raw === 'other' || raw === 'none'
    ? raw
    : 'none'
}

/** `qualityLimitationDurations` is cumulative seconds per reason. */
export function readDurations(raw: unknown): LimitationDurations {
  const d = (raw ?? {}) as Record<string, unknown>
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
  return { cpu: num(d.cpu), bandwidth: num(d.bandwidth), other: num(d.other) }
}

/**
 * Worst reason across simulcast rows.
 *
 * A producer reports one row per layer and they can disagree — the top rung
 * CPU-limited while the thumbnail is fine is the normal shape of trouble. Taking
 * the worst is what makes that visible; averaging or last-wins would hide it.
 */
export function worstReason(reasons: QualityLimitationReason[]): QualityLimitationReason {
  if (reasons.length === 0) return 'none'
  return REASONS[Math.min(...reasons.map((r) => REASONS.indexOf(r)))]
}

/**
 * How much of the last interval was spent limited, per reason, as 0..1.
 *
 * Cumulative counters are near-useless to look at — a call twenty minutes old
 * has large numbers whatever is happening now. The delta over the poll interval
 * is the thing that answers "is it struggling *currently*".
 */
export function limitedFractions(
  prev: LimitationDurations,
  now: LimitationDurations,
  seconds: number,
): LimitationDurations {
  if (!(seconds > 0)) return ZERO_DURATIONS
  const clamp = (v: number) => Math.max(0, Math.min(1, v))
  return {
    cpu: clamp((now.cpu - prev.cpu) / seconds),
    bandwidth: clamp((now.bandwidth - prev.bandwidth) / seconds),
    other: clamp((now.other - prev.other) / seconds),
  }
}
